<script setup lang="ts">
import { CalendarService } from '@/class/services/CalendarService'
import { CalendarUserSettings } from '@/class/services/CalendarUserSettings'
import User from '@/models/user'
import AppState, {
  fetchUserDetails,
  getCalendarUserSettings,
  getUsersInLocation,
  isPermissionsEnabled,
  invalidateCalendarUserSettingsCache,
  invalidateUserDetailsCache,
  updateUserPreferencesStore,
} from '@/states/app'
import { CalendarScopes } from '@/types'
import {
  normalizeMeetingLocationType,
  toStoredMeetingLocationType,
} from '@/utils/meetingLocationType'
import { computedTimezonesWithBusinessTimezone } from '@/utils/timezonesOptions'
import { shouldShowMeetingRoomOption } from '@/utils/meetingRoomVisibility'
import InfoCircleIcon from '@gohighlevel/ghl-icons/24/outline/InfoCircleIcon'
import {
  UIButton,
  UIForm,
  UIFormItem,
  UIInput,
  UISelect,
  UISpin,
  UITooltip,
} from '@gohighlevel/ghl-ui'
import axios from 'axios'
import { computed, h, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
const customMeetingLocationTypes = ['address', 'phone', 'custom']
const ungatedMeetingLocationTypes = [...customMeetingLocationTypes, 'lc_meet']
const meetingLocation = {
  address: '{{location.full_address}}',
  phone: '{{location.phone}}',
  custom: '',
}
const formValue = ref({
  meetingLocationType: 'custom',
  meetingLocation: '',
  tempMeetingLocation: '',
})

const isSaving = ref<boolean>(false)
const isLoading = ref<boolean>(false)
const selectedUserTimezone = ref<string>('')

const canSavePreferences = computed(() =>
  isPermissionsEnabled(CalendarScopes.EDIT_CALENDAR_SETTINGS)
)

const userDetail = computed(() => {
  return AppState?.loggedInUserDetail as User
})

const meetingLocationTypes = ref([
  {
    label: t('appointment_modal.custom'),
    value: 'custom',
    key: 'custom',
  },
  {
    label: t('meeting_location_types.lc_meet'),
    value: 'lc_meet',
    key: 'lc_meet',
  },
  {
    label: t('meeting_location_types.zoom'),
    value: 'true',
    key: 'zoom',
  },
  {
    label: t('meeting_location_types.google_meet'),
    value: 'meet',
    key: 'meet',
  },
  {
    label: t('meeting_location_types.phone'),
    value: 'phone',
    key: 'phone',
  },
  {
    label: t('meeting_location_types.full_address'),
    value: 'address',
    key: 'address',
  },
  {
    label: t('meeting_location_types.microsoft_teams'),
    value: 'ms_teams',
    key: 'ms_teams',
  },
])

const syncMeetingLocationFromUser = (user?: User) => {
  if (!AppState.locationId || !user) {
    return
  }

  const locationWiseMeetingLocation = user.locationWiseMeetingLocation
  const locationWiseZoomAdded = user.locationWiseZoomAdded

  formValue.value.meetingLocation =
    locationWiseMeetingLocation?.[AppState.locationId] ?? ''

  formValue.value.meetingLocationType = normalizeMeetingLocationType(
    locationWiseZoomAdded?.[AppState.locationId]
  )

  if (formValue.value.meetingLocationType === 'custom') {
    formValue.value.tempMeetingLocation = formValue.value.meetingLocation
  }
}

const loadData = async () => {
  if (!AppState.user?.id || !AppState.locationId) {
    return
  }

  isLoading.value = true
  const [userDetails, { data: userSettings }] = await Promise.all([
    fetchUserDetails(AppState.user?.id),
    getCalendarUserSettings(AppState.locationId, AppState.user?.id),
  ])
  syncMeetingLocationFromUser(userDetails || userDetail.value)

  isLoading.value = false

  const {
    data: { choices },
  } = await CalendarService.getAvailableMeetingLocationChoices(
    AppState.locationId
  )

  meetingLocationTypes.value = meetingLocationTypes.value.map(x => {
    return {
      label: x.label,
      value: x.value,
      key: x.key,
      disabled: ungatedMeetingLocationTypes.includes(x.key)
        ? false
        : !choices[x.key],
    }
  }).filter(
    x =>
      x.key !== 'lc_meet' ||
      shouldShowMeetingRoomOption(AppState.isMeetingRoomEnabled, [
        formValue.value.meetingLocationType,
      ])
  )

  selectedUserTimezone.value =
    userSettings?.userTimezonePreference?.value || 'business_timezone'
}

const changeMeetingLocation = value => {
  if (value === 'custom') {
    formValue.value.meetingLocation = formValue.value.tempMeetingLocation
  } else if (customMeetingLocationTypes.includes(value)) {
    formValue.value.meetingLocation = meetingLocation[value]
  }
}

onMounted(async () => {
  await loadData()
})

watch(
  () => AppState.user,
  async () => {
    await loadData()
  }
)

watch(
  () => AppState.locationId,
  async () => {
    await loadData()
  }
)

watch(
  () => userDetail.value,
  user => {
    syncMeetingLocationFromUser(user)
  },
  { immediate: true }
)

const savePreferences = async () => {
  isSaving.value = true
  try {
    await Promise.all([
      axios.put(`/users/${AppState.user?.id}`, {
        locationWiseZoomAdded: {
          ...userDetail.value?.locationWiseZoomAdded,
          [AppState.locationId]: toStoredMeetingLocationType(
            formValue.value.meetingLocationType
          ),
        },
        locationWiseMeetingLocation: {
          ...userDetail.value?.locationWiseMeetingLocation,
          [AppState.locationId]: formValue.value.meetingLocation,
        },
      }),
      CalendarUserSettings.updateUserSettingsFeatures({
        locationId: AppState.locationId,
        userId: AppState.user?.id,
        userTimezonePreference: {
          label:
            computedTimezonesWithBusinessTimezone.value
              .flatMap(x => x.children)
              .find(x => x.value === selectedUserTimezone.value)?.label ||
            selectedUserTimezone.value,
          value: selectedUserTimezone.value,
        },
      }),
    ])

    const userId = AppState.user?.id
    if (userId) {
      invalidateUserDetailsCache(userId)
      invalidateCalendarUserSettingsCache(AppState.locationId, userId)
    }
    await Promise.all([
      CalendarService.applyDefaultMeetingLocation(AppState.locationId),
      userId ? fetchUserDetails(userId, true, true) : Promise.resolve(),
      getUsersInLocation(true), // Force refresh the users state
    ])

    updateUserPreferencesStore()

    isSaving.value = false
  } catch (error) {
    console.error(error)
    isSaving.value = false
  }
  // toast('Changes Saved!')
}

const renderOption = ({ option, node }) => {
  if (option.key === 'business_group') {
    //add tooltip to the node
    const tooltip = h(
      UITooltip,
      {
        placement: 'right',
        class: 'max-w-sm',
        type: 'info',
      },
      {
        trigger: () => h(InfoCircleIcon, { class: 'w-4 text-gray-500' }),
        default: () =>
          h('div', { class: '' }, [
            h(InfoCircleIcon, { class: 'w-4' }),
            h('span', { class: 'text-sm ml-1' }, [
              t('settings.preferences.business_timezones_tooltip_1'),
            ]),
          ]),
      }
    )
    return h('div', { class: 'flex items-center' }, [node, tooltip])
  }
  return node
}

const timezoneOptions = computed(() => {
  const labelMap: Record<string, string> = {
    business_timezone: t('settings.preferences.business_timezones_1'),
    recommended: t('settings.preferences.recommended_timezones'),
    all: t('settings.preferences.all_timezones'),
  }

  const groupLabelMap: Record<string, string> = {
    recommended: t('settings.preferences.recommended_timezones'),
    business_group: t('settings.preferences.business_timezones_1'),
    all: t('settings.preferences.all_timezones'),
  }

  const options = computedTimezonesWithBusinessTimezone.value.map(group => ({
    ...group,
    label: groupLabelMap[group.key] ?? group.label,
    children: group.children.map(option => ({
      ...option,
      label: labelMap[option.value] ?? option.label,
    })),
  }))
  return options
})
</script>
<template>
  <div class="max-w-4xl">
    <div class="flex flex-col">
      <div class="mb-4 space-y-1">
        <div class="text-lg font-medium text-gray-900">
          {{ $t('settings.preferences.user_preferences') }}
        </div>
        <div class="text-sm text-gray-500">
          {{ $t('settings.preferences.set_preferences') }}
        </div>
      </div>
      <div
        class="grid grid-cols-3 items-baseline space-x-20 border-b-2 border-t-2 border-gray-200 pt-3"
      >
        <div class="">
          {{ $t('settings.preferences.scheduling_options') }}
        </div>
        <div class="flex flex-col">
          <UISpin v-if="isLoading" :show="true" />
          <UIForm v-else id="" style="width: 400px">
            <UIFormItem :label="$t('settings.preferences.meeting_location')">
              <UISelect
                id=""
                v-model:value="formValue.meetingLocationType"
                :options="meetingLocationTypes"
                :fallback-option="false"
                @update:value="value => changeMeetingLocation(value)"
              />
            </UIFormItem>

            <UIFormItem
              v-if="
                customMeetingLocationTypes.includes(
                  formValue.meetingLocationType
                )
              "
              :label="$t('settings.preferences.custom_meeting_location')"
            >
              <UIInput
                id="appointment-title-input"
                v-model="formValue.meetingLocation"
                :placeholder="$t('settings.preferences.meeting_location')"
              />
            </UIFormItem>
          </UIForm>
        </div>
      </div>
      <div
        class="grid grid-cols-3 items-baseline space-x-20 border-b-2 border-gray-200 pt-3"
      >
        <div class="">
          {{ $t('settings.preferences.view_appointments_in_1') }}
        </div>
        <div class="w-96">
          <UIFormItem
            :label="$t('settings.availability.timezone')"
            style="width: 400px"
          >
            <div class="flex flex-col">
              <UISelect
                id="select-timezone-dropdown-availability"
                v-model:value="selectedUserTimezone"
                :loading="isLoading"
                :options="timezoneOptions"
                :render-option="renderOption"
                :filterable="true"
                :placeholder="$t('placeholders.select_one')"
              />
              <p class="py-2 text-sm text-gray-500">
                {{ $t('settings.preferences.timezone_uses_details_1') }}
              </p>
            </div>
          </UIFormItem>
        </div>
      </div>
    </div>

    <UIButton
      v-if="canSavePreferences"
      id=""
      class="float-right mb-2 mt-6"
      :loading="isSaving"
      type="primary"
      @click="savePreferences"
    >
      {{ $t('settings.preferences.save_preferences') }}
    </UIButton>
  </div>
</template>
